// Sidebar items the fork adds (see components-next/sidebar/Sidebar.vue)
import { computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { useChat } from 'custom/chat/useChat';
import { showChatNotification } from 'custom/chat/notifications';

export function useCustomSidebarItems() {
  const { t } = useLocoI18n();
  const { accountScopedRoute } = useAccount();
  const { isAdmin } = useAdmin();
  const currentUser = useMapGetter('getCurrentUser');
  // The sidebar is always mounted, so it keeps team chat listening for messages and notifications
  const { joinedChannels, directChannels } = useChat({
    currentUserId: currentUser.value?.id,
    onNotify: showChatNotification(t),
  });

  const channelItem = channel => ({
    name: `TeamChat-${channel.id}`,
    label: channel.kind === 'direct' ? channel.name : `# ${channel.name}`,
    icon: channel.kind === 'private' ? 'i-lucide-lock' : undefined,
    badgeCount: channel.unread_count,
    to: accountScopedRoute('loco_team_chat_channel', { channelId: channel.id }),
  });

  const aiItem = () => ({
    name: 'LocoAi',
    label: t('LOCO.AI.SIDEBAR'),
    icon: 'i-lucide-sparkles',
    children: [
      {
        name: 'LocoAiAssistants',
        label: t('LOCO.AI.ASSISTANTS.TITLE'),
        icon: 'i-lucide-bot',
        activeOn: ['loco_ai_assistant'],
        to: accountScopedRoute('loco_ai_assistants'),
      },
      {
        name: 'LocoAiSettings',
        label: t('LOCO.AI.SETTINGS.TITLE'),
        icon: 'i-lucide-key-round',
        to: accountScopedRoute('loco_ai_settings'),
      },
      {
        name: 'LocoPixtaConnection',
        label: t('LOCO.PIXTA.SETTINGS.TITLE'),
        icon: 'i-lucide-ticket',
        to: accountScopedRoute('loco_pixta_connection'),
      },
    ],
  });

  return computed(() => [
    {
      name: 'TeamChat',
      label: t('LOCO.CHAT.SIDEBAR'),
      icon: 'i-lucide-messages-square',
      children: [
        {
          name: 'TeamChatHome',
          label: t('LOCO.CHAT.BROWSE'),
          icon: 'i-lucide-compass',
          to: accountScopedRoute('loco_team_chat'),
        },
        ...joinedChannels.value.map(channelItem),
        ...directChannels.value.map(channelItem),
      ],
    },
    ...(isAdmin.value ? [aiItem()] : []),
  ]);
}

// Upstream's Captain menu needs Chatwoot Enterprise; without it, it only leads to an upgrade page
export function useSidebarFilter() {
  const accountId = useMapGetter('getCurrentAccountId');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );
  return item =>
    item.name !== 'Captain' ||
    isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.CAPTAIN);
}
